import { isDistinctPlatformSessionChange } from '@/common/utils/pageDataFieldMerge'
import { isMainFrameOwnedSession } from '@/common/utils/mainFrameSlot'

const successorByRetiredSession = new Map<string, string>()
/** Server answered 4004 / core-null for this id. Do not send it again. */
const unreachableSessionIds = new Set<string>()

function asSessionId(value: unknown): string {
  if (value == null) return ''
  const text = String(value).trim()
  return text === '-1' ? '' : text
}

/**
 * doAction со status >= 1 подменяет core: middle удаляет request core и возвращает session temp core.
 * isClone (adjacent или core главной MAIN_FRAME) оставляет исходный core, его session закрывать нельзя.
 * Иначе rewrite переносит home на temp core, который следующий SAME_WINDOW уже не клонирует.
 */
export function actionRequestClonesSourceCore(
  pagesData: Record<string, unknown> | null | undefined,
  sessionId: unknown,
  adjacentSplit: boolean,
): boolean {
  return adjacentSplit || isMainFrameOwnedSession(pagesData, sessionId)
}

/**
 * Wire `isClone` for this action. NEW_DIALOG / NEW_WINDOW clone in elementAction
 * even when the opener is an ordinary document core. That source core stays mounted.
 */
export function requestClonesPlatformCore(input: {
  windowType?: unknown
  clonesSourceCore?: boolean
}): boolean {
  if (input.clonesSourceCore) return true
  return String(input.windowType ?? '').toUpperCase().includes('NEW')
}

export function shouldRetireActionRequestCore(input: {
  requestSessionId: unknown
  responseSessionId: unknown
  status: unknown
  clonedRequest: boolean
}): boolean {
  if (input.clonedRequest) return false
  const status = Number(input.status)
  if (!Number.isFinite(status) || status < 1) return false
  return isDistinctPlatformSessionChange(input.requestSessionId, input.responseSessionId)
}

/** Старый core уже снят. Следующий reload/action с его id должен идти в temp core, а не пропускаться. */
export function noteRetiredRequestCore(requestSessionId: unknown, responseSessionId: unknown): void {
  const from = asSessionId(requestSessionId)
  const to = asSessionId(responseSessionId)
  if (!from || !to || from === to) return
  successorByRetiredSession.set(from, to)
}

/** True when doAction already removed this id and replaced it with another core. */
export function isRetiredRequestCore(sessionId: unknown): boolean {
  const id = asSessionId(sessionId)
  return Boolean(id && successorByRetiredSession.has(id))
}

/** reload/panel or STACK already proved this id is not on the server. */
export function noteUnreachablePlatformSession(sessionId: unknown): void {
  const id = asSessionId(sessionId)
  if (id) unreachableSessionIds.add(id)
}

export function isUnreachablePlatformSession(sessionId: unknown): boolean {
  const id = asSessionId(sessionId)
  return Boolean(id && unreachableSessionIds.has(id))
}

export function resolveLivePlatformSessionId(sessionId: unknown): string | null {
  const start = asSessionId(sessionId)
  if (!start) return null
  let current = start
  const seen = new Set<string>()
  while (!seen.has(current)) {
    const next = successorByRetiredSession.get(current)
    if (!next || next === current) break
    seen.add(current)
    current = next
  }
  return current
}

export function entryUsesPlatformSession(entry: unknown, sessionId: string): boolean {
  if (!entry || typeof entry !== 'object') return false
  const record = entry as { sessionId?: unknown; $sessionId?: unknown; data?: unknown }
  const direct = String(record.sessionId ?? record.$sessionId ?? '').trim()
  if (direct === sessionId) return true
  const nested = record.data
  if (!nested || typeof nested !== 'object' || nested === entry) return false
  const inner = nested as { sessionId?: unknown; $sessionId?: unknown }
  return String(inner.sessionId ?? inner.$sessionId ?? '').trim() === sessionId
}

/**
 * Reload target after SAME_WINDOW replaces the request core.
 * A mounted replacement session is still the wizard core even if a sibling
 * unmount briefly flagged it closing. A retired id with no live replacement is skipped.
 */
export function resolveReloadTargetSessionId(
  sessionId: unknown,
  isClosing?: (sessionId: string) => boolean,
  isMounted?: (sessionId: string) => boolean,
): string | null {
  const live = resolveLivePlatformSessionId(sessionId)
  if (!live) return null
  if (unreachableSessionIds.has(live)) return null
  if (!isClosing?.(live)) return live
  if (isMounted?.(live)) return live
  return null
}

/** Point still-open pages at the temp core. Middleware already removed the request core. */
export function rewriteMountedPlatformSession(
  buckets: Array<Record<string, unknown> | null | undefined>,
  from: unknown,
  to: unknown,
): number {
  const source = asSessionId(from)
  const target = asSessionId(to)
  if (!source || !target || source === target) return 0
  let updated = 0
  const visit = (entry: unknown) => {
    if (!entry || typeof entry !== 'object') return
    const record = entry as Record<string, unknown>
    if (String(record.sessionId ?? '').trim() === source) {
      record.sessionId = target
      updated += 1
    }
    if (String(record.$sessionId ?? '').trim() === source) {
      record.$sessionId = target
      updated += 1
    }
    const nested = record.data
    if (nested && typeof nested === 'object' && nested !== entry) visit(nested)
  }
  for (const bucket of buckets) {
    if (!bucket) continue
    for (const entry of Object.values(bucket)) visit(entry)
  }
  return updated
}

export function resetRetiredRequestCoresForTests(): void {
  successorByRetiredSession.clear()
  unreachableSessionIds.clear()
}
